// PG Engine Extras — pure scanners that enrich the PG resolver with
// dialect-specific intelligence:
//
//   • `findInsertOnConflictTarget(source, statementStart, statementEnd)` —
//     locates the target table of `INSERT INTO target ... ON CONFLICT ...`
//     so the `excluded` pseudo-table can map to the target's columns.
//   • `getJsonbOperators()` — curated list of JSONB operators surfaced at
//     expression-position contexts (WHERE / SELECT-list / etc.).
//   • `getPgvectorOperators()` — pgvector distance operators surfaced when
//     the `vector` extension is installed.
//   • `findPlpgsqlDeclareVars(source, cursor)` — scans backward through any
//     active `$$...$$` (or `$tag$...$tag$`) body to find DECLARE blocks and
//     extract local variables. Best-effort: matches `name TYPE` lines at the
//     statement start, after DECLARE and before BEGIN.
//
// Pure module — no Monaco / electron imports. Operates on raw source text.

import { findEnclosingDollarQuotedBody } from '@/features/sql-grammar/document/editorContextScan';
import { tokenize, type Token } from '../context/tokenizer';
import { parseIdentifierToken } from '../resolver/identifierUtils';

const ENGINE = 'pgsql' as const;

export type PgsqlInsertTarget = {
  readonly schema?: string;
  readonly name: string;
};

/**
 * JSONB operators per PG docs (https://www.postgresql.org/docs/current/functions-json.html).
 * Surfaced at expression-position contexts (WHERE / SELECT-list / etc.) at
 * tier 6 (snippet) so a user typing after a JSONB column reference fuzzy-
 * matches `->`, `@>`, etc. The list is curated — bitwise operators and
 * arithmetic operators stay out of this catalog because they're handled by
 * the keyword set or by the snippet provider.
 */
export type PgsqlOperatorEntry = {
  readonly op: string;
  readonly description: string;
};

const JSONB_OPERATORS: readonly PgsqlOperatorEntry[] = [
  { op: '->', description: 'JSON: get field as JSON' },
  { op: '->>', description: 'JSON: get field as text' },
  { op: '#>', description: 'JSON: get path as JSON' },
  { op: '#>>', description: 'JSON: get path as text' },
  { op: '@>', description: 'JSONB: contains' },
  { op: '<@', description: 'JSONB: contained by' },
  { op: '?', description: 'JSONB: key/element exists' },
  { op: '?|', description: 'JSONB: any of keys exist' },
  { op: '?&', description: 'JSONB: all keys exist' },
  { op: '||', description: 'JSONB: concatenate' },
  { op: '#-', description: 'JSONB: delete path' },
];

const PGVECTOR_OPERATORS: readonly PgsqlOperatorEntry[] = [
  { op: '<->', description: 'pgvector: L2 (Euclidean) distance' },
  { op: '<#>', description: 'pgvector: negative inner product' },
  { op: '<=>', description: 'pgvector: cosine distance' },
  { op: '<+>', description: 'pgvector: L1 (taxicab) distance' },
];

export function getJsonbOperators(): readonly PgsqlOperatorEntry[] {
  return JSONB_OPERATORS;
}

export function getPgvectorOperators(): readonly PgsqlOperatorEntry[] {
  return PGVECTOR_OPERATORS;
}

/**
 * Find the target relation of an `INSERT INTO target ... ON CONFLICT ...`
 * statement. Returns null when the cursor's statement is not an INSERT, or
 * when no `ON CONFLICT` keyword sequence is present (the `excluded`
 * pseudo-table is only meaningful inside an `ON CONFLICT DO UPDATE` clause).
 *
 * The two-pass form (first scan for INSERT INTO target, then verify ON
 * CONFLICT exists) keeps the function side-effect-free and lets the caller
 * trust a non-null return as "you can resolve `excluded.col` to columns of
 * this table".
 */
export function findInsertOnConflictTarget(
  source: string,
  statementStart: number,
  statementEnd: number,
): PgsqlInsertTarget | null {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  let target: PgsqlInsertTarget | null = null;
  let sawOnConflict = false;
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (!upper) continue;
    if (!target && upper === 'INSERT' && identUpper(tokens[i + 1]) === 'INTO') {
      const t = readQualifiedName(tokens, i + 2);
      if (t) target = t.target;
    }
    if (upper === 'ON' && identUpper(tokens[i + 1]) === 'CONFLICT') {
      sawOnConflict = true;
    }
  }
  return sawOnConflict ? target : null;
}

function readQualifiedName(
  tokens: readonly Token[],
  startIdx: number,
): { target: PgsqlInsertTarget; lastIdx: number } | null {
  if (startIdx >= tokens.length) return null;
  const head = tokens[startIdx];
  if (!head || head.type !== 'ident') return null;
  const parts: string[] = [parseIdentifierToken(head.text).content];
  let i = startIdx + 1;
  while (i < tokens.length && tokens[i].type === 'dot') {
    const next = tokens[i + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
    parts.push(parseIdentifierToken(next.text).content);
    i += 2;
  }
  const name = parts[parts.length - 1]!;
  const schema = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  return { target: schema ? { schema, name } : { name }, lastIdx: i - 1 };
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────
// CREATE TYPE … AS RANGE ( <option> = ▮ ) option-value slot
// ─────────────────────────────────────────────────────────────────────────

/**
 * The `<option> = ▮` value slot of a `CREATE TYPE … AS RANGE (…)` body, for
 * the options whose value comes from a closed vocabulary rather than the
 * schema. Returns the option name (lower-cased) or null.
 *
 * Why a dedicated scanner: the clause machine has no CREATE-TYPE arm, so the
 * body lands on the `other` clause, and the `=` then reads as a comparison —
 * the value-slot recognizer claims the caret as `comparison-rhs` and the
 * shared value policy answers with `ALL` / `ANY` / `SOME` / `(SELECT …)`,
 * which are all illegal in a type definition. Recognising the real shape lets
 * the resolver answer with the option's own vocabulary instead
 * (pgsql script.40 §3).
 *
 * `subtype` is recognised as well, for the same reason: its value IS a type
 * name, but the comparison-RHS claim fires first and the data-type emission
 * further down the resolver is never reached, so the caret has to be named
 * here for the resolver to route it to the type list (pgsql script.40 §1/§2).
 *
 * The remaining RANGE options (`subtype_opclass`, `collation`,
 * `multirange_type_name`) are still unrecognised — an operator-class and a
 * collation name are catalog metadata the schema snapshot does not carry, so
 * naming the slot would only replace wrong answers with none.
 */
export type PgsqlRangeOption = 'subtype_diff' | 'canonical' | 'subtype';

export function findPgRangeOptionValueSlot(
  source: string,
  statementStart: number,
  cursor: number,
): PgsqlRangeOption | null {
  const tokens = tokenize(source, statementStart, cursor, ENGINE);
  if (tokens.length < 2) return null;
  // The caret must sit right after `<ident> =`, allowing for a partially typed
  // word (`subtype_diff = tsr▮`).
  let end = tokens.length;
  if (tokens[end - 1]!.type === 'ident') end -= 1;
  const eq = tokens[end - 1];
  if (!eq || eq.text !== '=') return null;
  const name = tokens[end - 2];
  if (!name || (name.type !== 'ident' && name.type !== 'kw')) return null;
  const option = name.text.toLowerCase();
  if (option !== 'subtype_diff' && option !== 'canonical' && option !== 'subtype') return null;
  // …inside a `CREATE TYPE … AS RANGE (` body. Scanned over the whole
  // statement prefix rather than a fixed window: the body is multi-line and
  // the options may be listed in any order.
  if (!hasCreateTypeAsRangeLead(tokens, end - 2)) return null;
  return option;
}

/** True when the token run before `beforeIdx` contains the
 *  `CREATE … TYPE <name> AS RANGE (` lead of the enclosing statement. */
function hasCreateTypeAsRangeLead(tokens: readonly Token[], beforeIdx: number): boolean {
  let sawCreateType = false;
  for (let i = 0; i < beforeIdx; i += 1) {
    if (identUpper(tokens[i]) !== 'CREATE') continue;
    // `CREATE TYPE` / `CREATE OR REPLACE TYPE`.
    for (let j = i + 1; j <= i + 3 && j < beforeIdx; j += 1) {
      if (identUpper(tokens[j]) === 'TYPE') sawCreateType = true;
    }
    if (sawCreateType) break;
  }
  if (!sawCreateType) return false;
  for (let i = 0; i < beforeIdx; i += 1) {
    if (identUpper(tokens[i]) === 'AS' && identUpper(tokens[i + 1]) === 'RANGE') return true;
  }
  return false;
}

/**
 * PG's built-in range subtype-difference functions — the complete set shipped
 * in `pg_catalog`, which is exactly what the `subtype_diff` option accepts
 * unless the user writes their own. Ordered by subtype family so the numeric
 * ones (the common case) come first.
 */
export const PG_RANGE_SUBTYPE_DIFF_FUNCTIONS: readonly string[] = [
  'int4range_subdiff',
  'int8range_subdiff',
  'numrange_subdiff',
  'daterange_subdiff',
  'tsrange_subdiff',
  'tstzrange_subdiff',
];

/**
 * PG's built-in range canonicalization functions. Only the discrete-subtype
 * ranges have one — a continuous range (numeric, timestamp) has no canonical
 * form, which is why the list is short.
 */
export const PG_RANGE_CANONICAL_FUNCTIONS: readonly string[] = [
  'int4range_canonical',
  'int8range_canonical',
  'daterange_canonical',
];

// ─────────────────────────────────────────────────────────────────────────
// `<schema>.▮` drill — is a ROUTINE legal here?
// ─────────────────────────────────────────────────────────────────────────

/**
 * The words that, sitting immediately in front of a schema qualifier, make the
 * drill a RELATION-ONLY slot: `ALTER TABLE public.▮`, `DROP TABLE IF EXISTS
 * public.▮`, `TRUNCATE TABLE public.▮`, `COMMENT ON TABLE public.▮`, `REFRESH
 * MATERIALIZED VIEW public.▮`, `CREATE TRIGGER … ON public.▮`, `CREATE INDEX …
 * ON public.▮`, `INSERT INTO public.▮`, `UPDATE public.▮`, `REFERENCES
 * public.▮`. A function name is a syntax error at every one of them.
 *
 * Deny-list rather than allow-list on purpose: a routine CALL is legal at
 * effectively every expression and FROM caret (`SELECT public.f(x)`, `WHERE
 * public.f(x)`, `FROM public.srf(1)`, `, public.f(y)`, `EXECUTE FUNCTION
 * public.f()`, `CALL public.p()`), so enumerating those would be enumerating
 * the language; the exclusions are the closed set.
 */
const PG_RELATION_ONLY_DRILL_LEADS: ReadonlySet<string> = new Set([
  'TABLE', 'VIEW', 'INTO', 'REFERENCES', 'ON', 'UPDATE', 'SEQUENCE', 'INDEX',
  'TRIGGER', 'EXISTS', 'ONLY', 'MATERIALIZED',
]);

/**
 * True when the caret's `<qualifier>.▮` drill sits at a slot that accepts a
 * relation and nothing else — so the resolver must not add that schema's
 * routines to the answer.
 */
export function isPgsqlRelationOnlyDrill(
  source: string,
  statementStart: number,
  cursor: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  let i = raw.length - 1;
  const last = raw[i];
  // An attached partial after the dot (`public.aca▮`).
  if (last && last.end === cursor && (last.type === 'ident' || last.type === 'kw')) i -= 1;
  if (i < 0 || raw[i]!.type !== 'dot') return false;
  i -= 2; // skip the dot and the qualifier itself
  const lead = raw[i];
  if (!lead || (lead.type !== 'ident' && lead.type !== 'kw')) return false;
  return PG_RELATION_ONLY_DRILL_LEADS.has(lead.text.toUpperCase());
}

/**
 * True when the caret's `<qualifier>.▮` drill is the routine reference of an
 * `EXECUTE FUNCTION|PROCEDURE` tail — the `CREATE TRIGGER … EXECUTE FUNCTION
 * public.▮` slot, whose only sensible answer is a `RETURNS trigger` function.
 */
export function isPgsqlExecuteFunctionDrill(
  source: string,
  statementStart: number,
  cursor: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  let i = raw.length - 1;
  const last = raw[i];
  if (last && last.end === cursor && (last.type === 'ident' || last.type === 'kw')) i -= 1;
  if (i < 0 || raw[i]!.type !== 'dot') return false;
  i -= 2;
  const lead = raw[i];
  if (!lead || (lead.type !== 'ident' && lead.type !== 'kw')) return false;
  const leadUpper = lead.text.toUpperCase();
  if (leadUpper !== 'FUNCTION' && leadUpper !== 'PROCEDURE') return false;
  const before = raw[i - 1];
  return !!before
    && (before.type === 'ident' || before.type === 'kw')
    && before.text.toUpperCase() === 'EXECUTE';
}

// ─────────────────────────────────────────────────────────────────────────
// PL/pgSQL `RAISE … , ▮` argument position
// ─────────────────────────────────────────────────────────────────────────

/**
 * True when the caret sits in the argument list of a PL/pgSQL `RAISE`
 * statement — `RAISE NOTICE 'Customer % spent %', v_customer_id, ▮`.
 *
 * RAISE is a procedural statement, not SQL: its arguments are expressions over
 * the block's locals and parameters. A bare relation name is a compile error
 * there, yet the caret classifies as a generic expression position and used to
 * be answered with the whole relation list on top of the locals
 * (pgsql script.37 §5).
 *
 * Requires a routine body around the caret: `RAISE` outside one is not a
 * statement at all, so there is nothing to recognise.
 */
export function isPgsqlRaiseArgument(
  source: string,
  statementStart: number,
  cursor: number,
): boolean {
  if (!findEnclosingDollarQuotedBody(source, cursor)) return false;
  const tokens = tokenize(source, statementStart, cursor, ENGINE);
  // Walk back from the caret to the nearest statement boundary. `RAISE` found
  // first means the caret is inside its argument list; a `;` first means the
  // RAISE (if any) is already closed. The backward walk is what handles a
  // RAISE that opens a block — statement isolation then starts at `BEGIN`,
  // `LOOP` or `THEN` rather than at the RAISE itself.
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.type === 'semicolon') return false;
    if (t.type !== 'ident' && t.type !== 'kw') continue;
    if (t.text.toUpperCase() !== 'RAISE') continue;
    // `RAISE ▮` itself is the LEVEL slot (NOTICE / EXCEPTION / …), not an
    // argument — leave that caret to the general keyword emission.
    return i < tokens.length - 1;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// Partial-index predicate — `CREATE INDEX … ON t (…) WHERE ▮`
// ─────────────────────────────────────────────────────────────────────────

/**
 * True when the caret sits in the WHERE predicate of a partial index.
 *
 * A `CREATE INDEX` has no FROM clause, so `deriveCompletionContext` binds no
 * aliases and the predicate-head policy has no row scope to offer columns
 * from — yet the indexed table IS the row scope of this predicate, and its
 * columns are the only column references legal here. Naming the shape lets the
 * resolver bind that table explicitly (pgsql script.31 §5).
 *
 * The match anchors on the `)` that closes the index's column/expression list,
 * so the optional `WITH (…)` storage parameters and `TABLESPACE <name>` that
 * may sit between it and `WHERE` are skipped, and a `WHERE` inside the
 * expression list itself (illegal in PG, but a half-typed statement can look
 * like it) cannot match — the closer has to come first.
 */
export function isPgsqlIndexPredicate(
  source: string,
  statementStart: number,
  cursor: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  if (!/^\s*CREATE\s+(?:UNIQUE\s+)?INDEX\b/i.test(slice)) return false;
  return /\)\s*(?:WITH\s*\([^)]*\)\s*)?(?:TABLESPACE\s+(?:"(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*)?WHERE\b/i
    .test(slice);
}

// ─────────────────────────────────────────────────────────────────────────
// DATE_TRUNC / DATE_PART field literal slot
// ─────────────────────────────────────────────────────────────────────────

/**
 * PG's date/time field vocabulary — the closed set `date_trunc` and
 * `date_part` accept as their first argument. Ordered coarse-to-fine (the
 * order the docs use and the order people reach for), which only survives to
 * the popup as an `orderHint`.
 *
 * `date_part` accepts a few fields `date_trunc` does not (`dow`, `doy`,
 * `epoch`, `isodow`, `isoyear`, `julian`, `timezone*`) because they are
 * extractions rather than truncation boundaries. They are appended after the
 * shared boundary fields and suppressed for `date_trunc`, so neither slot
 * offers a field its own function rejects.
 */
const PG_DATE_TRUNC_FIELDS: readonly string[] = [
  'millennium', 'century', 'decade', 'year', 'quarter', 'month', 'week',
  'day', 'hour', 'minute', 'second', 'milliseconds', 'microseconds',
];

const PG_DATE_PART_ONLY_FIELDS: readonly string[] = [
  'epoch', 'dow', 'isodow', 'doy', 'isoyear', 'julian',
  'timezone', 'timezone_hour', 'timezone_minute',
];

export type PgsqlDateFieldFunction = 'date_trunc' | 'date_part';

export function getPgDateFields(fn: PgsqlDateFieldFunction): readonly string[] {
  return fn === 'date_trunc'
    ? PG_DATE_TRUNC_FIELDS
    : [...PG_DATE_TRUNC_FIELDS, ...PG_DATE_PART_ONLY_FIELDS];
}

/**
 * `DATE_TRUNC('▮', ts)` / `DATE_PART('▮', ts)` — the cursor sits INSIDE the
 * first argument's string literal. Returns the function (lower-cased) or null.
 *
 * A string-literal position, so it has to be recognised by a dedicated
 * scanner and consulted BEFORE the resolver's string bail — the same shape
 * the enum-value and JSON-path collectors use. The literal is data to the
 * lexer but a closed keyword vocabulary to the user, and nothing else in the
 * completion surface can produce it (pgsql script.60 §2).
 *
 * Because the caret is inside the quotes, candidates are BARE words: the
 * surrounding `'…'` already exists in the buffer, so a quoted label would
 * insert `''month''`.
 *
 * Engine divergence (deliberate): mssql's `DATETRUNC`/`DATEPART` take the
 * field as an UNQUOTED token and are served by `isMssqlDatepartPosition`;
 * mysql spells it `EXTRACT(unit FROM d)`, also unquoted, so the ordinary
 * keyword emission reaches it. Oracle's `TRUNC(d, 'fmt')` is the closest
 * analogue with a quoted argument, but its vocabulary is the format-model
 * language (`'IW'`, `'MM'`, `'DDD'`, …), not these field names — a separate
 * list, not this one, and no coverage checkpoint asks for it yet. SQLite has
 * no equivalent function.
 */
export function findPgDateFieldLiteralSlot(
  source: string,
  statementStart: number,
  cursor: number,
): PgsqlDateFieldFunction | null {
  // Walk back to the opening quote of the literal the caret is inside.
  let i = cursor - 1;
  let quoteStart = -1;
  while (i >= statementStart) {
    const ch = source[i];
    if (ch === '\n') return null;
    if (ch === "'") {
      // A doubled `''` is an escaped quote inside the literal, not its start.
      if (source[i - 1] === "'") {
        i -= 2;
        continue;
      }
      quoteStart = i;
      break;
    }
    i -= 1;
  }
  if (quoteStart < 0) return null;
  // The literal must be the FIRST argument: `(` directly precedes the quote.
  let j = quoteStart - 1;
  while (j >= statementStart && /\s/.test(source[j]!)) j -= 1;
  if (j < statementStart || source[j] !== '(') return null;
  // Read the callee name.
  let k = j - 1;
  while (k >= statementStart && /\s/.test(source[k]!)) k -= 1;
  const nameEnd = k + 1;
  while (k >= statementStart && /[A-Za-z0-9_]/.test(source[k]!)) k -= 1;
  const name = source.slice(k + 1, nameEnd).toLowerCase();
  // A qualified call (`myschema.date_trunc(`) is a user routine.
  if (source[k] === '.') return null;
  if (name === 'date_trunc') return 'date_trunc';
  if (name === 'date_part') return 'date_part';
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// PL/pgSQL DECLARE variable extraction
// ─────────────────────────────────────────────────────────────────────────

export type PlpgsqlVariable = {
  readonly name: string;
  readonly dataType?: string;
  /** Source range of the variable's declaration line. */
  readonly definedAt: { readonly start: number; readonly end: number };
};

export type PlpgsqlBlock = {
  /** Source range covering the body BETWEEN the dollar quotes (exclusive). */
  readonly bodyStart: number;
  readonly bodyEnd: number;
  readonly variables: readonly PlpgsqlVariable[];
};

/**
 * Find the active `$$...$$` (or `$tag$...$tag$`) PL/pgSQL block enclosing
 * `cursor`, if any, and extract DECLARE-block variables. Returns null when
 * the cursor is outside any dollar-quoted body. Best-effort: only matches
 * simple `name [CONSTANT] type [:= default];` declarations between DECLARE
 * and BEGIN. Nested blocks are not handled — the outermost block wins.
 *
 * The body span itself comes from the shared
 * `findEnclosingDollarQuotedBody` scan in `editorContextScan.ts` — the same
 * one `deriveCompletionContext` uses to move its analysis window inside the
 * body — so the two cannot disagree about where a body starts and ends. What
 * this adds on top is the DECLARE-block variable extraction.
 */
export function findEnclosingPlpgsqlBlock(
  source: string,
  cursor: number,
): PlpgsqlBlock | null {
  const block = findEnclosingDollarQuotedBody(source, cursor);
  if (!block) return null;
  const vars = extractDeclareVariables(source, block.bodyStart, block.bodyEnd);
  return { bodyStart: block.bodyStart, bodyEnd: block.bodyEnd, variables: vars };
}

/**
 * Extract DECLARE variables from a PL/pgSQL body. Looks for `DECLARE` at the
 * top of the body (or after labels like `<<label>> DECLARE`), then scans
 * `name [CONSTANT] [type] [:= default];` lines until `BEGIN`.
 */
function extractDeclareVariables(
  source: string,
  bodyStart: number,
  bodyEnd: number,
): readonly PlpgsqlVariable[] {
  // Walk the body to find DECLARE / BEGIN keywords. PL/pgSQL is line-based
  // so we tokenize via the shared SQL tokenizer (good enough for keyword
  // recognition) and walk the token stream.
  const tokens = tokenize(source, bodyStart, bodyEnd, ENGINE);
  let declareIdx = -1;
  let beginIdx = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (!upper) continue;
    if (declareIdx === -1 && upper === 'DECLARE') {
      declareIdx = i;
      continue;
    }
    if (declareIdx !== -1 && upper === 'BEGIN') {
      beginIdx = i;
      break;
    }
  }
  if (declareIdx === -1 || beginIdx === -1) return [];
  return parseDeclarationLines(tokens, declareIdx + 1, beginIdx);
}

function parseDeclarationLines(
  tokens: readonly Token[],
  from: number,
  to: number,
): readonly PlpgsqlVariable[] {
  const out: PlpgsqlVariable[] = [];
  let i = from;
  while (i < to) {
    // Each declaration ends at `;` (or end-of-region). Read a leading
    // identifier as the variable name; the next non-CONSTANT, non-DEFAULT,
    // non-:= token is the type.
    const head = tokens[i];
    if (!head || head.type !== 'ident') {
      i = advanceToSemicolon(tokens, i, to);
      if (i < to) i += 1;
      continue;
    }
    const declStart = head.start;
    const name = parseIdentifierToken(head.text).content;
    let j = i + 1;
    // Skip optional CONSTANT.
    if (j < to && identUpper(tokens[j]) === 'CONSTANT') j += 1;
    // Capture the next ident/kw run as the type (until `:=`, `DEFAULT`, or `;`).
    let type = '';
    const typeStart = j;
    while (j < to) {
      const t = tokens[j];
      if (!t) break;
      if (t.type === 'semicolon') break;
      const u = identUpper(t);
      if (u === 'DEFAULT') break;
      // `:=` shows up as op token text; bail on assignment.
      if (t.type === 'op' && t.text === ':=') break;
      j += 1;
    }
    if (j > typeStart) {
      const parts: string[] = [];
      for (let k = typeStart; k < j; k += 1) {
        const t = tokens[k];
        if (!t) continue;
        if (t.type === 'ident' || t.type === 'kw') parts.push(t.text);
      }
      type = parts.join(' ');
    }
    const declEnd = tokens[j]?.end ?? declStart;
    out.push({
      name,
      dataType: type || undefined,
      definedAt: { start: declStart, end: declEnd },
    });
    // Advance past the next semicolon.
    i = advanceToSemicolon(tokens, j, to);
    if (i < to) i += 1;
  }
  return out;
}

function advanceToSemicolon(
  tokens: readonly Token[],
  from: number,
  to: number,
): number {
  let i = from;
  while (i < to) {
    if (tokens[i].type === 'semicolon') return i;
    i += 1;
  }
  return to;
}
